import type { ItemsQuery } from "@mondaydotcomorg/api";
import { PARENT_TASKS_COLUMNS, TASKS_COLUMNS } from "@onesolution/constants";
import type { Context } from "@onesolution/middlewares";
import {
	ALL_MONDAY_SCHEMAS,
	MONDAY_PAGINATED_PARAMS_DEF,
	MONDAY_PAGINATED_PARAMS_USE,
	MONDAY_SCHEMAS,
	type MONDAY_TYPES,
} from "@onesolution/monday";
import { gql } from "graphql-request";
import type { DefaultPaginatedQueryParams } from "../defaultPaginatedParams";
import { formatPaginatedResult } from "../format";
import { requestAndFormat } from "../utils";

type Task = MONDAY_TYPES["base"] & {
	parentItem: MONDAY_TYPES["base"] & {
		briefs: { linked_items: { id: string; name: string }[] };
	};
	name: MONDAY_TYPES["text"];
	deadline: MONDAY_TYPES["date"];
	who: MONDAY_TYPES["status"];
	status: MONDAY_TYPES["status"];
	type: MONDAY_TYPES["status"];
};

export const tasksQuery = (
	context: Context,
	params: DefaultPaginatedQueryParams & {
		queryParams: ItemsQuery;
	},
) =>
	requestAndFormat(
		{
			defaultParams: {
				boardId: [context.boards?.tasks],
				columnIds: [
					TASKS_COLUMNS.name,
					TASKS_COLUMNS.deadline,
					TASKS_COLUMNS.type,
					TASKS_COLUMNS.status,
					TASKS_COLUMNS.who,
				],
				parentColumnIds: [PARENT_TASKS_COLUMNS.briefs],
			},
			gql: gql`
            query getTasks(
                $boardId: [ID!],
                $columnIds: [String!]!,
                $parentColumnIds: [String!]!,
				${MONDAY_PAGINATED_PARAMS_DEF(params.cursor, "$queryParams: ItemsQuery")}
            ) {
				boards(ids: $boardId) {
					items_page(
						${MONDAY_PAGINATED_PARAMS_USE(params.cursor, "query_params: $queryParams")}
					) {
						cursor
						items {
							id
							name
							column_values(ids: $columnIds) {
								${ALL_MONDAY_SCHEMAS}
							}

							parent_item {
								name
								column_values(ids: $parentColumnIds) {
									${MONDAY_SCHEMAS.base}

									... on BoardRelationValue {
										linked_items {
											id
											name
										}
									}
								}
							}
						}
					}
				}
            }
        `,
			resultKey: "boards[0].items_page.items",
			additionalFormatting: formatPaginatedResult<Task>(
				"boards[0].items_page.cursor",
			),
			outType: {} as {
				cursor: string;
				items: Task[];
			},
		},
		params,
	);

export const tasksCountQuery = (
	context: Context,
	params: {
		queryParams: ItemsQuery;
	},
) =>
	requestAndFormat(
		{
			defaultParams: {
				boardId: context.boards?.tasks,
				columnIds: [TASKS_COLUMNS.status],
			},
			gql: gql`
			 query getTasksCount(
                $boardId: [ID!],
                $columnIds: [String!]!,
				$queryParams: ItemsQuery
            ) {
				boards(ids: $boardId) {
					items_page(
						limit: 500,
						query_params: $queryParams
					) {
						cursor
						items {
							id
							column_values(ids: $columnIds) {
								${MONDAY_SCHEMAS.base}
							}
						}
					}
				}
            }`,
			resultKey: "boards[0].items_page.items",
			outType: [] as Array<
				MONDAY_TYPES["base"] & {
					status: MONDAY_TYPES["status"];
				}
			>,
		},
		params,
	);
